import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { LayoutDashboard, Users, FileCheck, PawPrint, Layers, ShoppingCart, Settings, LogOut, BarChart3, ShieldCheck } from "lucide-react";
import LogoMark from "../LogoMark";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "nav-admin-dashboard", exact: true },
  { to: "/admin/kyc", label: "KYC Queue", icon: FileCheck, testid: "nav-admin-kyc" },
  { to: "/admin/customers", label: "Customers", icon: Users, testid: "nav-admin-customers" },
  { to: "/admin/animals", label: "Animals", icon: PawPrint, testid: "nav-admin-animals" },
  { to: "/admin/lots", label: "Lots", icon: Layers, testid: "nav-admin-lots" },
  { to: "/admin/sales", label: "Sales", icon: ShoppingCart, testid: "nav-admin-sales" },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, testid: "nav-admin-reports" },
  { to: "/admin/config", label: "Configuration", icon: Settings, testid: "nav-admin-config" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  const isActive = (n) => n.exact ? location.pathname === n.to : location.pathname.startsWith(n.to);

  return (
    <div className="min-h-screen bg-[#F8F4E9] flex">
      <aside className="w-64 hidden md:flex flex-col border-r border-[#E8E0D2] bg-white">
        <div className="p-6 border-b border-[#E8E0D2]">
          <Link to="/admin" className="block">
            <LogoMark size="md" />
            <div className="text-[10px] uppercase tracking-widest text-[#6B5F58] mt-2 font-semibold">Admin Console</div>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => {
            const active = isActive(n);
            return (
              <Link key={n.to} to={n.to} data-testid={n.testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active ? "bg-[#FCEDED] text-[#D71921]" : "text-[#6B5F58] hover:bg-[#F8F4E9] hover:text-[#1F1816]"
                }`}>
                <n.icon className="w-4 h-4" /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-[#E8E0D2]">
          <div className="text-xs text-[#6B5F58] mb-2">Signed in as</div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold">{user?.username || user?.full_name}</div>
          </div>
          <button onClick={handleLogout} data-testid="admin-logout-btn"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-[#E8E0D2] text-sm hover:bg-[#F8F4E9]">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col">
        <header className="md:hidden sticky top-0 z-30 bg-white border-b border-[#E8E0D2] px-4 h-14 flex items-center justify-between">
          <div className="font-display text-lg">Admin</div>
          <button onClick={handleLogout} className="text-[#6B5F58]"><LogOut className="w-5 h-5" /></button>
        </header>

        {/* Mobile horizontal nav */}
        <nav className="md:hidden bg-white border-b border-[#E8E0D2] overflow-x-auto">
          <div className="flex gap-1 px-3 py-2 whitespace-nowrap">
            {nav.map((n) => {
              const active = isActive(n);
              return (
                <Link key={n.to} to={n.to}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                    active ? "bg-[#FCEDED] text-[#D71921]" : "text-[#6B5F58]"
                  }`}>
                  <n.icon className="w-3.5 h-3.5" /> {n.label}
                </Link>
              );
            })}
          </div>
        </nav>

        <main className="flex-1 p-4 md:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
